import createClient from "openapi-fetch";

import type { paths } from "./schema";

/** The SPA's own sign-in screen, `routes/Login.tsx` (.docs/04 section 4). */
export const LOGIN_PATH = "/entrar";

/** allauth's TOTP enrolment form. Server-rendered, unlike the login screen. */
export const MFA_SETUP_PATH = "/accounts/2fa/totp/activate/";

/**
 * Set by `RequireMFAMiddleware` on the 403 it returns to an account that has
 * not enrolled a second factor. A header, not a body field, so this can be read
 * without cloning and parsing every 403 the API returns — most of which are
 * ordinary permission denials that must *not* navigate anywhere.
 */
export const MFA_REQUIRED_HEADER = "X-MFA-Required";

/**
 * Read a cookie *at call time*, never once at module load.
 *
 * Logging in rotates the CSRF token, and Django rejects the old one with a 403
 * that reads like a permissions bug. A snapshot taken when the bundle parsed is
 * stale by the time the first mutation runs.
 */
function getCookie(name: string): string | null {
  const prefix = `${name}=`;
  for (const part of document.cookie.split("; ")) {
    if (part.startsWith(prefix)) {
      return decodeURIComponent(part.slice(prefix.length));
    }
  }
  return null;
}

/**
 * Production sets `CSRF_COOKIE_NAME = "__Secure-csrftoken"`; local development
 * has no TLS and keeps Django's default. Try the hardened name first so the two
 * environments run identical code.
 */
export function getCsrfToken(): string | null {
  return getCookie("__Secure-csrftoken") ?? getCookie("csrftoken");
}

/**
 * The CSRF header, for the one request that cannot go through `client`.
 *
 * A file upload has to be a `FormData` body with a browser-generated multipart
 * boundary, which `openapi-fetch` cannot express — so that call uses `fetch`
 * directly and needs the header the middleware below would have added.
 */
export function csrfHeader(): Record<string, string> {
  const token = getCsrfToken();
  return token ? { "X-CSRFToken": token } : {};
}

const SAFE_METHODS = new Set(["GET", "HEAD", "OPTIONS", "TRACE"]);

/** True once a redirect is under way, so concurrent failures do not stack. */
let redirecting = false;

/**
 * Send the browser to the sign-in screen, remembering where it was.
 *
 * A full navigation, not a router push, although the screen is a route of this
 * same app: a session that expired takes its query cache, its socket and its
 * softphone with it, and a page load is the one way to drop all three at once.
 * `next` is built from `window.location` only — never from a value in the
 * response — because an attacker-controlled `next` is an open redirect.
 */
export function redirectToLogin(): void {
  if (redirecting) return;
  redirecting = true;
  const next = window.location.pathname + window.location.search;
  window.location.assign(`${LOGIN_PATH}?next=${encodeURIComponent(next)}`);
}

/**
 * Leave for the sign-in screen after a deliberate sign-out.
 *
 * No `next`: whoever signs in at this browser afterwards may not be the person
 * who just left, and has no business being dropped on their last screen.
 *
 * Not behind the latch, unlike its neighbours. The session is gone by now, so
 * a badge poll that was in flight may already have answered 401 and started
 * the navigation that *does* carry a `next` — and the last `assign` before the
 * page unloads is the one that counts. Taking the latch afterwards keeps the
 * stragglers from overriding this one in turn.
 */
export function leaveForLogin(): void {
  redirecting = true;
  window.location.assign(LOGIN_PATH);
}

/**
 * Send the browser to allauth's TOTP enrolment.
 *
 * Signed in, but the account owes a second factor (.docs/06 section 9 item 13).
 * Bouncing to the login page instead would loop: the session is perfectly
 * valid, so the login screen would hand them straight back here.
 */
export function redirectToMfaSetup(): void {
  if (redirecting) return;
  redirecting = true;
  window.location.assign(MFA_SETUP_PATH);
}

/**
 * The `baseUrl` is the origin only — no `/api`. The generated paths already
 * start with `/api/`, because drf-spectacular emits the URLs Django actually
 * serves, so a base with a path in it would produce `/api/api/…`. Trimming the
 * prefix in the schema instead would break the Swagger UI at `/api/docs/`,
 * which resolves "try it out" against those same paths.
 *
 * `credentials: "same-origin"` because the SPA, the API and the socket share an
 * origin (.docs/04 section 1) and the session cookie rides along with no CORS
 * involved. `include` would be the cross-origin answer to a problem we
 * deliberately do not have.
 */
export const client = createClient<paths>({
  // The SPA's own origin, stated rather than implied. The generated paths are
  // already absolute-from-root, so this only fixes the origin — and it is the
  // one place the same-origin decision is enforced in code instead of prose.
  baseUrl: window.location.origin,
  credentials: "same-origin",
  // Looked up per call rather than captured once. openapi-fetch would otherwise
  // bind whatever `globalThis.fetch` was when this module was imported, which
  // makes the client impossible to intercept — in tests, and equally for any
  // later instrumentation that wants to wrap it.
  fetch: (request) => globalThis.fetch(request),
  // `?status__in=1,2`, not `?status__in=1&status__in=2`. openapi-fetch explodes
  // arrays by default, and django-filter's `__in` reads a single comma-separated
  // parameter — handed the repeated form it keeps the *last* value and drops
  // the rest without a word, so a manager's agenda asked for pending and
  // approved work and got approved only. The schema says the same thing
  // (`explode: false` on every `__in`); openapi-fetch does not read it.
  querySerializer: { array: { style: "form", explode: false } },
});

client.use({
  onRequest({ request }) {
    if (!SAFE_METHODS.has(request.method.toUpperCase())) {
      const token = getCsrfToken();
      if (token) request.headers.set("X-CSRFToken", token);
    }
    return request;
  },
  onResponse({ response }) {
    // Sessions expire. Without this the SPA renders empty lists and the seller
    // concludes their data is gone (.docs/04 section 4).
    if (response.status === 401) redirectToLogin();
    // A 403 is normally a permission denial and stays on screen. This one is
    // the server saying "finish enrolling first", and there is nothing on this
    // page the seller can do about it.
    else if (response.status === 403 && response.headers.has(MFA_REQUIRED_HEADER)) {
      redirectToMfaSetup();
    }
    return response;
  },
});

/** Test seam: the module-level latch would otherwise leak between test cases. */
export function __resetRedirectLatch(): void {
  redirecting = false;
}

/**
 * Thrown when a request fails. `openapi-fetch` returns errors rather than
 * throwing, but TanStack Query needs a rejection to move a query into
 * `isError` — returning the error object would cache it as a success.
 */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status?: number,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/**
 * Pull the sentence DRF actually wrote out of an error body.
 *
 * This matters more than it looks. The server refuses a free-form WhatsApp
 * reply outside the 24-hour window with a specific explanation, and refuses a
 * call with the reason it cannot be placed. Replacing those with "algo salió
 * mal" throws away the only part the agent can act on.
 */
function detailFrom(error: unknown): string | null {
  if (typeof error !== "object" || error === null) return null;
  for (const [key, value] of Object.entries(error as Record<string, unknown>)) {
    if (key === "detail" && typeof value === "string") return value;
    // `{"detail": ["…"]}` and `{"non_field_errors": ["…"]}` are the same thing
    // wearing different keys, depending on whether the view or the serializer
    // raised it.
    if (Array.isArray(value) && typeof value[0] === "string") return value[0];
    // A list of rows answers row by row — `{"phones": [{}, {"phone": ["…"]}]}`
    // for a bad second number — so the message is one level further down.
    // Only objects are searched: a bare string body is an HTML error page, not
    // something to put in front of a seller.
    const rows = Array.isArray(value) ? value : [value];
    for (const row of rows) {
      const nested = detailFrom(row);
      if (nested) return nested;
    }
  }
  return null;
}

export async function unwrap<T>(
  promise: Promise<{ data?: T; error?: unknown; response?: Response }>,
  fallback: string,
): Promise<T> {
  const { data, error, response } = await promise;
  if (error !== undefined || data === undefined) {
    throw new ApiError(detailFrom(error) ?? fallback, response?.status);
  }
  return data;
}
